import React, { useState } from 'react';
import {
    ApptDetailRow,
    CustomizedSwitch,
    DateRow,
    DateRowStartEnd,
    DisplayIdRow,
    LinkRow, LocalDateRow,
    MinLoader,
    PriceRow,
    SimpleTooltip,
    StatusCard,
    StatusRow,
    TagBox,
    TagComponent,
    TextRow,
    TypeRow,
} from 'components';
import {
    Colors,
    Images,
    manageType,
    PermissionList,
    renderStaffName,
    RolePermission,
    states,
    tagEnums,
    useSecondaryModal,
} from 'utils';
import { adminActions } from 'store';

const { showDashIfEmpty } = hooksForTable;
import { hooksForTable, makeCapitalize } from 'utils';

import { residencyEnumTypes } from '../../createStaff/core/constants';
import { StaffAssignment } from './staffAssignment';
import { containsSSN, maskSSN } from '../../../history/constants';
import { defaultStatuses } from '../../../../pages/client/constants';
import { ScheduleDetails } from '../../../schedule';
import { Route } from 'react-router-dom';
import { CreateForm, TimeSheetDetailPage } from '../../../../pages';
import { OpenClaimDetails } from '../../../../pages/billing/claim/openClaimDetails';

export const staffTabsLabels = (checkTimesheetTab, checkMySettings) => [
    RolePermission([PermissionList.STAFF_READ.code, PermissionList.STAFF_CREATE.code]) && { label: 'General' },
    RolePermission([PermissionList.STAFF_PAYCODE_MANAGE?.code, PermissionList.STAFF_PAYCODE_READ?.code]) && { label: 'Paycodes' },
    checkTimesheetTab && { label: 'Timesheet' },
    RolePermission([PermissionList.STAFF_CREDENTIAL_MANAGE?.code, PermissionList.STAFF_CREDENTIAL_READ?.code]) && { label: 'Credentials' },

    RolePermission([PermissionList.STAFF_READ.code]) && { label: 'Availability' },

    RolePermission([PermissionList.STAFF_ROLES_MANAGE?.code]) && { label: 'Access' },

    RolePermission([PermissionList.CLIENT_STAFF_ASSIGNMENT_READ?.code, PermissionList.CLIENT_STAFF_ASSIGNMENT?.code]) && { label: 'Assigned Clients' },

    RolePermission([PermissionList.STAFF_SERVICE_MANAGE?.code, PermissionList.STAFF_SERVICE_READ?.code]) && { label: 'Blocked Services' },

    checkMySettings && { label: 'My Settings' },

    { label: 'Comments' },
    RolePermission([PermissionList.STAFF_NOTE_MANAGE?.code, PermissionList.STAFF_NOTE_READ?.code]) && { label: 'Notes' },
    RolePermission([PermissionList.STAFF_HISTORY_READ?.code]) && { label: 'History' },
    RolePermission([PermissionList.STAFF_FILE_MANAGE?.code, PermissionList.STAFF_FILE_READ?.code]) && { label: 'Files' },
];

export const staffHeaderTitles = [
    { title: 'Date', sortable: true },
    { title: 'Creator Name', sortable: true },
    { title: 'Subject', sortable: false },
    { title: 'Action', sortable: false },
];

export function getStaffGeneralInfo({ info, changePassword }) {
    return [
        { title: 'Secondary Email', value: info?.secondaryEmail },
        {
            title: 'Secondary Phone Number',
            value: info?.secondaryPhone
                ? '(' +
                info?.secondaryPhone.substr(0, 3) +
                ') ' +
                info?.secondaryPhone.substr(3, 3) +
                '-' +
                info?.secondaryPhone.substr(6, 6)
                : '',
        },
        {
            title: 'Address',
            value: `${info?.address?.formattedAddress} ${info?.address?.unitNumber ? ` / ${info?.address?.unitNumber}` : ''}`,
        },
        {
            title: 'Languages Spoken',
            value: info?.languages?.length ? info?.languages?.map((i) => `${i}`).join(', ') : '',
        },
        {
            title: 'Hired Date',
            value: info?.hiredDate ? <DateRow date={info?.hiredDate} /> : null,
        },
        {
            title: 'Status ',
            value: (
                <div style={{ marginTop: 2 }}>
                    <StatusRow status={info?.status} />
                </div>
            ),
            type: 'multiple',
            list: [
                {
                    title: 'Status',
                    value: <StatusRow status={info?.status} />,
                },
                info?.termination?.date && {
                    title: 'Status Date',
                    value: <DateRow date={info?.termination?.date} />,
                },
                info?.termination?.reason && {
                    title: 'Reason',
                    value: <TextRow name={info?.termination?.reason} />,
                },
            ],
        },
        RolePermission([PermissionList.STAFF_SET_PASSWORD?.code]) && {
            title: '',
            type: 'renderValue',
            value: (
                <button onClick={changePassword} className="set-staff-password">
                    <img src={Images.staffPassword} alt="" />
                    <p>Set Password</p>
                </button>
            ),
        },
    ].filter((item) => !!item?.value);
}

export function getStaffEmploymentInfo(info, pastEmployments) {
    if (info) {
        return [
            { title: 'Title', value: info?.title?.name },
            {
                title: 'Supervisor',
                value: info?.supervisor ? renderStaffName(info?.supervisor) : '',
            },
            { title: 'Start Date', value: hooksForTable.handleCreatedAtDate(info?.startDate) },
            { title: 'Department', value: info?.department?.name },
            { title: 'Employment Type', value: manageType(info?.type) },
            { title: 'Past Employments', value: info?.address?.zip },
            { title: 'Past Employments', value: 'view history' },
        ].filter((item) => !!item.value);
    } else {
        return pastEmployments?.length > 0 ? [{ title: 'Past Employments', value: 'view history' }] : [];
    }
}

export function getStaffOtherDetails(info) {
    const checkTb = info?.credentials?.find((i) => i?.type === 'TB');
    const checkDoj = info?.credentials?.find((i) => i?.type === 'DOJ');
    const residency = residencyEnumTypes?.find((i) => i?.id === info?.residency);
    const issuingState = states?.find((i) => i?.id === info?.license?.state);

    const [show, setShow] = useState(false);

    return [
        {
            title: 'Gender & Date of Birth',
            value:
                (info?.gender || info?.birthday) &&
                `${info?.gender ? `${info?.gender},` : ''} ${info?.birthday ? hooksForTable.handleCreatedAtDate(info?.birthday) : ''}`,
        },
        {
            title: 'Driver License',
            value: info?.license?.driverLicense
                ? `${info?.license?.driverLicense},
             ${info?.license?.state ? ` ${issuingState?.name}, ` : ''}
              ${info?.license?.expireDate ? `EXP:${hooksForTable.handleCreatedAtDate(info?.license?.expireDate)}` : ''}`
                : '',
        },
        { title: 'NPI Number', value: info?.npi },
        { title: 'Taxonomy Code', value: info?.taxonomyCode },
        {
            title: 'Require Supervision',
            value: info?.requiresSupervision ? 'Yes' : '',
            type: info?.requiresSupervision ? 'multiple' : undefined,
            list: info?.requiresSupervision ? [
                { title: 'Require Supervision', value: 'Yes' },
                { title: 'Minimum Supervision %', titleWeight: 400, value: info?.minSupervisionPercent != null ? `${info.minSupervisionPercent}` : 'Org Default' },
                { title: 'Minimum Contacts per Month', titleWeight: 400, value: info?.minContactsPerMonth != null ? `${info.minContactsPerMonth}` : 'Org Default' },
            ] : undefined,
        },
        { title: 'Residency Status', value: residency?.label },
        RolePermission([PermissionList.STAFF_SSN_VIEW?.code]) && {
            title: 'SSN Number',
            value: info?.ssn ? (
                <div className="space-between" style={{ alignItems: 'center', marginTop: '-3px' }}>
                    <p style={{ width: 85 }}>{show ? info?.ssn : '**********'}</p>
                    <button onClick={() => setShow(!show)} className="reset-btn" style={{ padding: 0, height: 30 }}>
                        <img src={show ? Images.showEye : Images.hideEye} alt="icon" />
                    </button>
                </div>
            ) : null,
        },

        info?.credentials?.length
            ? {
                title: 'TB',
                value: 'a',
                type: 'multiple',
                list: [
                    {
                        title: 'TB',
                        value: (
                            <div className="flex-align-center">
                                <img
                                    src={checkTb?.checked ? Images.checkCredential : Images.uncheckCredential}
                                    alt="icon"
                                    style={{ marginRight: '4px' }}
                                />
                                <p>{checkTb?.checked ? 'checked' : 'not checked'}</p>
                            </div>
                        ),
                    },
                    {
                        title: 'Start Date',
                        titleWeight: 400,
                        value: checkTb?.startDate ? hooksForTable.handleCreatedAtDate(checkTb?.startDate) : 'Not Set',
                    },
                    {
                        title: 'Experation Date',
                        titleWeight: 400,
                        value: checkTb?.expireDate ? hooksForTable.handleCreatedAtDate(checkTb?.expireDate) : 'Not Set',
                    },
                ],
            }
            : {
                title: 'DOJ',
                value: null,
            },
        info?.credentials?.length
            ? {
                title: 'DOJ',
                value: 'a',
                type: 'multiple',
                list: [
                    {
                        title: 'DOJ',
                        value: (
                            <div className="flex-align-center">
                                <img
                                    src={checkDoj?.checked ? Images.checkCredential : Images.uncheckCredential}
                                    alt="icon"
                                    style={{ marginRight: '4px' }}
                                />
                                <p>{checkDoj?.checked ? 'checked' : 'not checked'}</p>
                            </div>
                        ),
                    },
                    {
                        title: 'Selected Date',
                        titleWeight: 400,
                        value: checkDoj?.startDate ? hooksForTable.handleCreatedAtDate(checkDoj?.startDate) : 'Not Set',
                    },
                ],
            }
            : {
                title: 'DOJ',
                value: null,
            },
    ].filter((item) => !!item?.value);
}

export const editButtonStyle = {
    height: '36px',
    paddingInline: '24px',
};

export const filterBtnStyle = {
    width: '93px',
    height: '36px',
};

// Credential Table config
export const FBI = 'DOJ/FBI';
export const TB = 'TB';

export const credentialBody = [
    {
        rowText: (item) => {
            if (item?.credential?.name === FBI || item?.credential?.name === TB) {
                return (
                    <SimpleTooltip title={<p>Default Credential</p>} placement="top-start">
                        <p className="credential-default-name">
                            <img src={Images.starVector} alt="icon" />
                            <TextRow name={item?.credential?.name} />
                        </p>
                    </SimpleTooltip>
                );
            } else {
                return <TextRow name={item?.credential?.name} />;
            }
        },
        width: '20%',
    },
    { rowText: (item) => <TypeRow text={item?.credential?.type} />, width: '15%' },
    { rowText: (item) => <DateRow date={item?.receiveDate ? item?.receiveDate : ''} />, width: '15%' },
    {
        rowText: (item) =>
            item?.expirationDate ? (
                <DateRow date={item?.expirationDate} />
            ) : (
                <span style={{ color: Colors.TextLightGray }}>Non-Expiring</span>
            ),
        width: '15%',
    },
    { rowText: (item) => <TextRow name={item?.licenseNumber} />, width: '20%' },
    RolePermission([PermissionList.STAFF_CREDENTIAL_MANAGE?.code]) && {
        button: (cb, item, it) => (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <img style={{ cursor: 'pointer', marginRight: '16px' }} src={Images.edit} alt="edit"
                     onClick={() => cb('editCredential')} />
                {item?.credential?.name !== FBI && item?.credential?.name !== TB && (
                    <img style={{ cursor: 'pointer' }} src={Images.remove} alt="remove"
                         onClick={() => cb('deleteCredential')} />
                )}
            </div>
        ),
        notClickable: true,
        width: '15%',
    },
];

export const ACTION_TYPE = 'GET_CREDENTIAL';

// Timesheet Table config
export const timesheetHead = [
    { name: 'displayId', title: 'ID', custom: false, searchKey: 'DISPLAY_ID' },
    { name: 'apptDisplayId', title: 'Appt ID', custom: false, disabled: true },
    { name: 'timesheetDate', title: 'Date', iconType: 'date', disabled: true },
    { name: 'cptCode', title: 'CPT Code', custom: false, disabled: true },
    {
        name: 'type',
        title: 'Type',
        disabled: true,
        filterList: [
            { name: 'All', id: 'All' },
            { name: 'Hourly', id: 'HOURLY' },
            { name: 'Salary', id: 'SALARY' },
            { name: 'Fixed', id: 'FIXED' },
            { name: 'Mileage', id: 'MILEAGE' },
        ],
    },
    { name: '', title: 'Paycode', custom: false, disabled: true },
    { name: '', title: 'Rate', custom: false, disabled: true },
    { name: '', title: 'Qty', custom: false, disabled: true },
    { name: '', title: 'Total AMT', custom: false, disabled: true },
    {
        name: 'status',
        title: 'Status',
        width: '100px',
        filterList: [
            { name: 'All', id: 'All' },
            { name: 'Active', id: 'ACTIVE' },
            { name: 'Processed', id: 'PROCESSED' },
        ],
        disabled: true,
    },
    RolePermission([PermissionList.STAFF_TIMESHEET_CREATE?.code]) && {
        name: '',
        title: ' ',
        custom: false,
        noSearch: true,
        width: '50px',
    },
];

export const timesheetBody = (afterSuccess) => [
    { rowText: (item) => <DisplayIdRow id={item?.displayId} />, notClickable: true },
    {
        rowText: (item) =>
            item?.apptDisplayId ? (
                <ApptDetailRow name={item?.apptDisplayId} id={item?.apptId} showWidth={'100%'}
                               afterSuccess={afterSuccess} />
            ) : (
                '--'
            ),
        notClickable: true,
    },
    // { rowText: (item) => <DisplayIdRow id={item?.apptDisplayId ? item?.apptDisplayId : '--'} />, notClickable: true },
    { rowText: (item) => <DateRow date={item?.timesheetDate} />, notClickable: true },
    { rowText: (item) => <TextRow name={item?.cptCode ? item?.cptCode : '--'} />, notClickable: true },
    { rowText: (item) => <TypeRow text={item?.type ? item?.type : '--'} />, notClickable: true },
    { rowText: (item) => <TextRow name={item?.payCode ? item?.payCode : '--'} />, notClickable: true },
    { rowText: (item) => <PriceRow info={item?.amount} />, notClickable: true },
    { rowText: (item) => <TextRow name={item?.qty ? item?.qty?.toFixed(2) : '--'} />, notClickable: true },
    { rowText: (item) => <PriceRow info={item?.totalAmount} />, notClickable: true },
    {
        rowText: (item) => <StatusCard status={item?.status} />,
        // <TypeRow text={item?.status} />,
        notClickable: true,
    },
    RolePermission([PermissionList.STAFF_TIMESHEET_CREATE?.code]) && {
        button: (cb, item) =>
            item?.apptDisplayId ? (
                ' '
            ) : (
                <button className="reset-btn" onClick={() => cb('edit')}>
                    <img src={Images.edit} alt={'icon'} />
                </button>
            ),
    },
];

export const timesheetDetailHead = [
    { name: 'rateType', title: 'Rate Type', custom: false, noSearch: true },
    { name: 'rate', title: 'Rate', custom: false, noSearch: true },
    { name: 'hours', title: 'Hours', custom: false, noSearch: true },
    { name: 'amount', title: 'Amount', custom: false, noSearch: true },
];

const renderRate = (item) => {
    const rate = !!item?.amount && !!item?.hours ? item.amount / item.hours : item?.payCode?.rate;
    return rate;
};
export const timesheetDetailBody = [
    {
        rowText: (item) => (
            <TypeRow
                text={item?.payCode?.payCodeTypeId?.type ? showDashIfEmpty(makeCapitalize(item?.payCode?.payCodeTypeId?.type)) : 'Fixed'}
            />
        ),
    },
    { rowText: (item) => <PriceRow info={renderRate(item)} /> },
    {
        rowText: (item) => <TextRow
            name={item.regularHours === 'NaNhNaNm' ? '--' : item.regularHours || item?.hours} />,
    },
    { rowText: (item) => <PriceRow info={item?.totalAmount} /> },
];

export const TIMESHEET_ACTION_TYPE = 'GET_TIMESHEET';

export const pastEmploymentHead = [
    { name: '', title: 'Employment', custom: false, noSearch: true },
    { name: '', title: 'Supervisor', custom: false, noSearch: true },
    { name: '', title: 'Period', custom: false, noSearch: true },
    { name: '', title: 'Department', custom: false, noSearch: true },
    { name: '', title: 'Type', custom: false, noSearch: true },
    RolePermission([PermissionList.STAFF_CREATE?.code]) && {
        name: '',
        title: 'Action',
        custom: false,
        noSearch: true,
        width: '100px',
    },
];

export const pastEmploymentBody = [
    { rowText: (item) => <TextRow name={item?.title?.name} textWidth={8} /> },
    { rowText: (item) => <TextRow name={renderStaffName(item?.supervisor)} textWidth={8} /> },
    { rowText: (item) => <DateRowStartEnd start={item?.startDate} end={item?.endDate} /> },
    { rowText: (item) => <TextRow name={item?.department?.name} textWidth={8} /> },
    { rowText: (item) => <TypeRow text={item?.type} /> },
    RolePermission([PermissionList.STAFF_CREATE?.code]) && {
        button: (fn) => (
            <div className="flex-align-center" style={{ gap: 16 }}>
                <button onClick={() => fn('edit')} className="reset-btn" style={{ padding: 0 }}>
                    <img src={Images.edit} alt="icon" />
                </button>
                <button onClick={() => fn('delete')} className="reset-btn">
                    <img src={Images.redTrash} alt="icon" />
                </button>
            </div>
        ),
    },
];

export const EMPLOYMENT_TYPES = [
    { name: 'Full', id: 'FULL' },
    { name: 'Part', id: 'PART' },
    { name: 'Intern', id: 'INTERN' },
    { name: 'Contractor', id: 'CONTRACTOR' },
];

export const staffStatus = {
    PENDING: 1,
    ACTIVE: 2,
    INACTIVE: 3,
};

export const staffPaycodeNotYet = {
    title: 'No Paycodes Yet',
    subTitle: 'Add paycodes to get started.',
    image: Images.noNote,
};

export const staffTimesheetNotYet = {
    title: 'No Timesheets Available',
    subTitle: 'Start by adding timesheets to track work hours.',
    image: Images.noHistory,
};

export const staffCredentialNotYet = {
    title: 'No Credentials Added',
    subTitle: 'Add credentials to track this staff member’s qualifications.',
    image: Images.noClientYet,
};

export const staffAccessNotYet = {
    title: 'No Roles Assigned',
    subTitle: 'Assign roles to define this staff member’s access and permissions.',
    image: Images.noAccess,
};

export const staffServiceNotYet = {
    title: 'No Blocked Services',
    subTitle: 'All payer services are currently available for this staff member.',
    image: Images.noNote,
};

export const clientAssignmentHead = [
    { name: '', title: 'Client', custom: false },
    { name: '', title: 'DOB', custom: false },
    { name: '', title: 'City, ZIP', custom: false },
    { name: '', title: 'Funder', custom: false },
    { name: '', title: 'Assigned Date', custom: false },
    { name: '', title: 'Assigned Role', custom: false },
    { name: '', title: 'Tags', custom: false },
    { name: '', title: 'Status', custom: false },
    RolePermission([PermissionList.CLIENT_STAFF_ASSIGNMENT?.code]) &&
    { name: 'action', title: 'Action', custom: false, disabled: true, width: '100px' },
];

export const clientAssignmentBody = [
    {
        rowText: (item) => (
            <LinkRow
                name={item?.clientName ? item.clientName : '--'}
                href={RolePermission([PermissionList.CLIENT_READ?.code]) ? `/client/${item?.clientId}` : ''}
                checkPermission={item?.clientId ? RolePermission([PermissionList.CLIENT_READ?.code]) : null}
            />
        ),
    },
    {
        rowText: (item) => <DateRow date={item?.clientDob} />,
    },
    {
        rowText: (item) => (
            <TextRow
                name={
                    item?.clientAddress?.city || item?.clientAddress?.zip
                        ? `${item?.clientAddress?.city} ${item?.clientAddress?.zip}`
                        : '--'
                }
                textWidth={10}
            />
        ),
    },
    {
        rowText: (item) => (
            <LinkRow
                name={
                    item?.enrollment?.payerShortName || item?.enrollment?.payerName
                        ? item?.enrollment?.payerShortName || item?.enrollment?.payerName
                        : '--'
                }
                href={RolePermission([PermissionList.FS_READ.code]) ? `/fundingSource/${item?.enrollment?.payerId}` : ''}
                checkPermission={item?.enrollment?.payerId ? RolePermission([PermissionList.FS_READ.code]) : null}
            />
        ),
    },
    { rowText: (item) => <DateRow date={item?.assignmentDate} /> },
    { rowText: (item) => <TextRow name={item?.staffRole} /> },
    {
        rowText: (item) =>
            item?.tags?.length ? (
                <div className="assigned-client-tags">
                    <TagBox tags={item?.tags} count={1} />
                </div>
            ) : (
                '--'
            ),
        notClickable: true,
    },
    { rowText: (item) => <StatusCard status={item?.clientStatus} /> },
    RolePermission([PermissionList.CLIENT_STAFF_ASSIGNMENT?.code]) &&
    {
        button: (cb) => (
            <div className="flex-align-center" style={{ gap: 16 }}>
                <div onClick={() => cb('edit')} style={{ height: '22px' }}>
                    <img src={Images.edit} alt="edit" />
                </div>
                <div onClick={() => cb('delete')} style={{ height: '22px' }}>
                    <img src={Images.remove} alt="edit" />
                </div>
            </div>
        ),
        notClickable: true,
    },
];

export const clientAssignmentNotYet = {
    title: 'No Client Assigned',
    subTitle: 'Assign client to get started',
    image: Images.noContact,
};

export const CLIENT_ASSIGN_ACTION_TYPE = 'GET_STAFF_ASSIGNED_CLIENTS';


export const staffHistoryTabEnums = {
    ON_STAFF: 'ON_STAFF',
    BY_STAFF: 'BY_STAFF',
};
export const staffHistoryTabTypes = [
    { title: 'Changes on Staff', tabValue: staffHistoryTabEnums.ON_STAFF },
    { title: 'Actions by Staff', tabValue: staffHistoryTabEnums.BY_STAFF },
];

export const stffHistoryHead = [
    { name: '', title: 'Date & Time', iconType: 'date', noSearch: true, width: '180px' },
    {
        name: 'onModel', title: 'Model', noSearch: true, disabled: true, width: '180px',
        filterList: [
            { name: 'All', id: 'All' },
            { name: 'Staff', id: 'Staff' },
            { name: 'Client', id: 'Client' },
            { name: 'Funder', id: 'Funder' },
            { name: 'Service', id: 'Service' },
            { name: 'Claim', id: 'Claim' },
            { name: 'Invoice', id: 'Invoice' },
            { name: 'Timesheet', id: 'Timesheet' },
            { name: 'Appt', id: 'Appt' },
            { name: 'Resource', id: 'Resource' },
            { name: 'Assessment', id: 'Assessment' },
            { name: 'Goal', id: 'Goal' },
            { name: 'Library Goal', id: 'LibraryGoal' },
            { name: 'Goal Domain', id: 'GoalDomain' },
            { name: 'Target', id: 'Target' },
            { name: 'Option', id: 'Option' },
            { name: 'Library Folder', id: 'LibraryFolder' },
            { name: 'Trial', id: 'Trial' },
            { name: 'CSession', id: 'CSession' },
            { name: 'TSummary', id: 'TSummary' },
            { name: 'Session Abc', id: 'SessionAbc' },
            { name: 'CEvent', id: 'CEvent' },
            { name: 'Tag', id: 'Tag' },
            { name: 'PR', id: 'PR' },
            { name: 'PReport', id: 'PReport' },
            { name: 'Forms', id: 'Forms' },
        ],
    },
    { name: 'resource', title: 'Resource', width: '200px', noSearch: true, custom: false },
    { name: 'desc', title: 'Action Description', custom: false, noSearch: true },
];
export const stffHistoryBody = [
    {
        rowText: (item) => (
            <div className={'flex'}>
                <LocalDateRow date={item?.createdDate} />
            </div>
        ),
    },
    { rowText: (item) => <TextRow name={item?.onModel} /> },
    { rowText: (item) => renderLink(item) },
    // { rowText: (item) => <TextRow name={item?.resourceInfo} /> },
    { rowText: (item) => <TextRow name={containsSSN(item?.title) ? maskSSN(item?.title) : item?.title} textWidth={7} />, },
];

const renderLink = (item) => {
    const { openSecondary } = useSecondaryModal();

    switch (item?.onModel) {
        case 'Appt':
            return (
                <div
                    className="link-btn"
                    onClick={() => openSecondary(<ScheduleDetails currentId={item?.resource}
                                                                  getAfterSuccess={() => getAppt('noLoad')} />)}
                >
                    {item?.resourceInfo}
                </div>
            );
        case 'Client':
            return (
                <LinkRow
                    name={item?.resourceInfo || '--'}
                    href={RolePermission([PermissionList.CLIENT_READ.code]) ? `/client/${item?.resource}` : ''}
                    checkPermission={item?.resource ? RolePermission([PermissionList.CLIENT_READ.code]) : null}
                />
            );
        case 'Staff':
            return (
                <LinkRow
                    name={item?.resourceInfo || '--'}
                    href={RolePermission([PermissionList.STAFF_READ.code]) ? `/staff/${item?.resource}` : ''}
                    checkPermission={item?.resource ? RolePermission([PermissionList.STAFF_READ.code]) : null}
                />
            );
        case 'Funder':
            return (
                <LinkRow
                    name={item?.resourceInfo || '--'}
                    href={RolePermission([PermissionList.FS_READ.code]) ? `/fundingSource/${item?.resource}` : ''}
                    checkPermission={item?.resource ? RolePermission([PermissionList.FS_READ.code]) : null}
                />
            );
        case 'Claim':
            return (
                <OpenClaimDetails claimId={item?.resource} displayId={item?.resourceInfo} />
                // <LinkRow
                //     name={item?.resourceInfo || '--'}
                //     href={RolePermission([PermissionList.CLAIMS_READ?.code]) ? `/claim/${item?.resource}` : ''}
                //     checkPermission={item?.resource ? RolePermission([PermissionList.CLAIMS_READ.code]) : null}
                // />
            );
        case 'Timesheet':
            return (
                <LinkRow
                    name={item?.resourceInfo || '--'}
                    href={RolePermission([PermissionList.TIMESHEET_READ?.code]) ? `/timesheet/${item?.resource}` : ''}
                    checkPermission={item?.resource ? RolePermission([PermissionList.TIMESHEET_READ.code]) : null}
                />
            );
        case 'Forms':
            return (
                <LinkRow
                    name={item?.resourceInfo || '--'}
                    href={RolePermission([PermissionList.MANAGE_CUSTOM_FORM?.code]) ? `/template/${item?.resource}` : ''}
                    checkPermission={item?.resource ? RolePermission([PermissionList.MANAGE_CUSTOM_FORM.code]) : null}
                />
            );


        default:
            return <TextRow name={item?.resourceInfo || '--'} />;
    }
};

export const STAFF_ACTION_TYPE = 'GET_STAFF_HISTORY';